import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useLdReady } from '@omnea-app/fe-org-settings/useLdReady'
import { useRouteLdFlags } from './launch-darkly'

/**
 * Which permissions system answers for the caller. `fgaAuthoritative` is the organisation's
 * enforcement switch: OpenFGA decides access. `permissionsConfigAvailable` is the caller's
 * configuration switch: the Permissions settings surface exists for them, whether or not their
 * organisation enforces yet.
 *
 * `pending` is LaunchDarkly not having answered. Its flags read `false` until then, and acting on
 * that would apply the role-based rules for a moment and flip to OpenFGA's answer once it resolves.
 */
export type AuthzRollout =
  | { status: 'pending' }
  | { status: 'ready'; fgaAuthoritative: boolean; permissionsConfigAvailable: boolean }

const PENDING: AuthzRollout = { status: 'pending' }

export const AuthzRolloutContext = createContext<AuthzRollout | undefined>(undefined)

export const AuthzRolloutProvider = ({ children }: { children: ReactNode }) => {
  const isLdReady = useLdReady()
  const { RELEASE_FGA_ENFORCEMENT, RELEASE_FGA_CONFIG } = useRouteLdFlags()

  const rollout = useMemo<AuthzRollout>(
    () =>
      isLdReady
        ? {
            status: 'ready',
            fgaAuthoritative: RELEASE_FGA_ENFORCEMENT === true,
            permissionsConfigAvailable: RELEASE_FGA_CONFIG === true,
          }
        : PENDING,
    [isLdReady, RELEASE_FGA_ENFORCEMENT, RELEASE_FGA_CONFIG]
  )

  return <AuthzRolloutContext.Provider value={rollout}>{children}</AuthzRolloutContext.Provider>
}

export const useAuthzRollout = (): AuthzRollout => {
  const rollout = useContext(AuthzRolloutContext)
  if (rollout == null) {
    throw new Error('useAuthzRollout must be used within an AuthzRolloutProvider')
  }
  return rollout
}
